import { useLayoutEffect, useState } from 'react';

/**
 * `useHorizontalOverflow(paneRef)` → `{ overflows, scrollWidth }` — does this
 * x-scroller's content overflow it, and by how much. THE measurement behind a
 * "render the sticky horizontal bar only while it overflows" rule
 * (`ui/StickyScrollbar`'s contract: the CALLER decides).
 *
 * Extracted 2026-10-05 from `components/budget/BudgetTableScroller.js` (moved,
 * not rewritten) when the Crew Gantt's page-scroll mode needed the same answer
 * for its timeline — owner: *"can you use common code from other gantt chart
 * pages?"*.
 *
 * - Measured `scrollWidth > clientWidth` (+0.5 for fractional widths), re-run by
 *   a `ResizeObserver` on the pane AND its first child (the content), because
 *   zoom, the sidebar and column widths all change it without a scroll event.
 * - 🪤 `clientWidth` 0 means NOTHING LAID OUT (a `display: none` ancestor, or
 *   jsdom), never "fits" or "overflows": `overflows` is `false` there, so tests
 *   see no bar unless they stub the measurements.
 * - No dependency array on the effect, deliberately: a late-mounting pane (a
 *   conditional render) is picked up on the next render, the same reason
 *   `hooks/useDragScroll` promotes its element into state.
 */
export default function useHorizontalOverflow(paneRef) {
  const [scrollWidth, setScrollWidth] = useState(0);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const pane = paneRef.current;
    if (!pane) return undefined;
    const measure = () => {
      const sw = pane.scrollWidth;
      const cw = pane.clientWidth;
      const next = cw > 0 && sw > cw + 0.5;
      setOverflows((o) => (o === next ? o : next));
      setScrollWidth((w) => (w === sw ? w : sw));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(pane);
    if (pane.firstElementChild) ro.observe(pane.firstElementChild);
    return () => ro.disconnect();
  });

  return { overflows, scrollWidth };
}
